import { z } from 'zod';
import {
    buildStoredJsonParser,
    defineStoredJsonContract,
    type StoredJsonRowBoundary,
} from '../../stored-json-contract.types';
import {
    type CloverCnpTokenSaleChargeBody,
    type CloverCnpTokenSaleChargeOutcomeBody,
    CloverCnpTokenSaleNetworkStatus,
    CloverCnpTokenSaleOutcomeType,
    type CloverCnpTokenSaleSourceBody,
    CloverCnpTokenSaleStatus,
    type CloverTransactionDetailsCardTransactionBody,
    type CloverTransactionDetailsPaymentBody,
    CloverTransactionDetailsPaymentResult,
    type CloverTransactionDetailsRefundBody,
    type CloverTransactionDetailsTenderBody,
    type CloverRefundBody,
    type CloverRefundCardTransactionBody,
    type CloverCloudPayRefundSuccessfulResponseDetails,
    type CloverEcommerceRefundResponseBody,
    type CloverEcommerceRefundSuccessfulResponseDetails,
    type CloverRefundErrorBody,
    type CloverRefundMalformedResponseDetails,
    CloverRefundOutcome,
    type CloverRefundPaymentBody,
    CloverRefundPaymentResult,
    type CloverRefundReferenceBody,
    type CloverRefundRejectedResponseDetails,
    type CloverRefundResponseBody,
    type CloverRefundResponseDetails,
    type CloverRefundSuccessfulResponseDetails,
    CloverLifecycleFinalizationOutcome,
    type CloverLifecycleFinalizationNormalizedResult,
    type CloverLifecycleFinalizationResponseDetails,
    type CloverLifecycleFinalizationResponsePayload,
    CloverLifecyclePreflightOutcome,
    type CloverLifecyclePreflightNormalizedResult,
    type CloverLifecyclePreflightResponseDetails,
    type CloverLifecyclePreflightResponsePayload,
    type CloverVoidCardTransactionBody,
    type CloverCloudPayVoidResponseBody,
    type CloverEcommerceVoidResponseBody,
    type CloverVoidErrorBody,
    type CloverVoidMalformedResponseDetails,
    CloverVoidOutcome,
    type CloverVoidPaymentBody,
    CloverVoidPaymentResult,
    type CloverVoidReferenceBody,
    type CloverVoidRejectedResponseDetails,
    type CloverVoidResponseBody,
    type CloverVoidResponseDetails,
    CloverVoidStatus,
    type CloverVoidSuccessfulResponseDetails,
    GatewayLifecycleResponseType,
    type CloverWebhookPaymentRetrievalResponseSnapshot,
    type GatewayLifecycleResponse,
    type GatewayLifecycleResponseJsonBody,
    type ProviderGatewayResponseSnapshot,
    type ValorCardMetadataSnapshot,
    type ValorTaxComponentSnapshot,
    type ValorTaxDetailsSnapshot,
    type ValorTxnFetchGatewayResponseSnapshot,
    type ValorTxnFetchRecordSnapshot,
    type ValorVcStatusGatewayResponseSnapshot,
} from '../../types/ipg/gateway-lifecycle-response.types';
import {
    CloverRefundKind,
    CloverLifecycleFinalizationGatewayOperation,
    CloverLifecyclePreflightGatewayOperation,
    PaymentGatewayTerminalSaleStatus,
} from '../../types/ipg/gateway-lifecycle-request.types';
import type { JsonObject } from '../../common.types';
import { JsonObjectSchema, JsonValueSchema } from '../common.schemas';
import {
    extractStoredJsonNumberToken,
    normalizeStoredJsonNumberFieldsAtPath,
    normalizeStoredJsonSafeIntegerFieldsAtPath,
    normalizeStoredJsonSafeIntegerObjectFields,
    StoredJsonNumberRecordSchema,
    StoredJsonNumberSchema,
} from '../../stored-json-number';
import {
    CloverCnpTokenSaleAmountSchema,
    CloverCnpTokenSaleCurrencySchema,
    CloverRefundAmountSchema,
    CloverTerminalSaleResponseLifecyclePayloadSchema,
    CloverVoidReasonSchema,
} from './gateway-lifecycle-request.schemas';
import { omitEmptyCloverCardTransactionText } from './clover/clover-card-transaction.schemas';

const CloverMinorUnitMaximum = 999999999;
const HttpStatusMinimum = 100;
const HttpStatusMaximum = 599;
const LifecycleNumericMagnitudeMaximum = Number.MAX_SAFE_INTEGER;

const CloverStoredTransactionDetailsAmountFields: readonly string[] = [
    'amount',
    'taxAmount',
    'tipAmount',
];

const CloverStoredTransactionDetailsTimestampFields: readonly string[] = [
    'createdTime',
];

export const CloverTransactionDetailsPaymentResultSchema: z.ZodType<CloverTransactionDetailsPaymentResult> =
    z.enum(CloverTransactionDetailsPaymentResult);

const CloverTransactionDetailsAmountSchema = z
    .number()
    .int()
    .min(0)
    .max(999999999);

const CloverTransactionDetailsTimestampSchema = z
    .number()
    .int()
    .min(0)
    .max(Number.MAX_SAFE_INTEGER);

const CloverTransactionDetailsCardTransactionBodyFieldsSchema = z.object({
    authCode: z.string().min(1).optional(),
    cardType: z.string().min(1).optional(),
    cardholderName: z.string().min(1).optional(),
    last4: z.string().regex(/^\d{4}$/).optional(),
    referenceId: z.string().min(1).optional(),
    state: z.string().min(1).optional(),
    token: z.string().min(1).optional(),
    transactionNo: z.string().min(1).optional(),
    type: z.string().min(1).optional(),
});

export const CloverTransactionDetailsCardTransactionBodySchema: z.ZodType<CloverTransactionDetailsCardTransactionBody> =
    z.preprocess(
        omitEmptyCloverCardTransactionText,
        CloverTransactionDetailsCardTransactionBodyFieldsSchema
    );

export const CloverTransactionDetailsTenderBodySchema: z.ZodType<CloverTransactionDetailsTenderBody> =
    z.object({
        id: z.string().min(1).optional(),
        label: z.string().min(1).optional(),
        labelKey: z.string().min(1).optional(),
    });

export const CloverTransactionDetailsRefundBodySchema: z.ZodType<CloverTransactionDetailsRefundBody> =
    z.object({
        id: z.string().min(1).optional(),
        amount: CloverTransactionDetailsAmountSchema.optional(),
        createdTime: CloverTransactionDetailsTimestampSchema.optional(),
        externalReferenceId: z.string().min(1).max(12).optional(),
        payment: z.object({ id: z.string().min(1).optional() }).optional(),
        taxAmount: CloverTransactionDetailsAmountSchema.optional(),
        tipAmount: CloverTransactionDetailsAmountSchema.optional(),
        voided: z.boolean().optional(),
    });

export const CloverTransactionDetailsPaymentBodySchema: z.ZodType<CloverTransactionDetailsPaymentBody> =
    z.object({
        id: z.string().min(1),
        amount: CloverTransactionDetailsAmountSchema.optional(),
        result: CloverTransactionDetailsPaymentResultSchema.optional(),
        cardTransaction: CloverTransactionDetailsCardTransactionBodySchema.optional(),
        createdTime: CloverTransactionDetailsTimestampSchema.optional(),
        externalPaymentId: z.string().min(1).max(32).optional(),
        externalReferenceId: z.string().min(1).max(12).optional(),
        offline: z.boolean().optional(),
        refunds: z.array(CloverTransactionDetailsRefundBodySchema).optional(),
        taxAmount: CloverTransactionDetailsAmountSchema.optional(),
        tipAmount: CloverTransactionDetailsAmountSchema.optional(),
        tender: CloverTransactionDetailsTenderBodySchema.optional(),
        voided: z.boolean().optional(),
    });

function normalizeStoredCloverTransactionDetailsNumericFields(
    value: unknown
): unknown {
    const rootResult = StoredJsonNumberRecordSchema.safeParse(value);
    if (!rootResult.success) {
        return value;
    }
    const amountNormalizedRoot = normalizeStoredJsonSafeIntegerObjectFields(
        rootResult.data,
        {
            domain: 'clover-transaction-details.amount',
            fields: CloverStoredTransactionDetailsAmountFields,
            minimum: 0,
            maximum: 999999999,
        }
    );
    const timestampNormalizedRoot = normalizeStoredJsonSafeIntegerObjectFields(
        amountNormalizedRoot,
        {
            domain: 'clover-transaction-details.timestamp',
            fields: CloverStoredTransactionDetailsTimestampFields,
            minimum: 0,
            maximum: Number.MAX_SAFE_INTEGER,
        }
    );
    const normalizedRootResult = StoredJsonNumberRecordSchema.safeParse(
        timestampNormalizedRoot
    );
    if (!normalizedRootResult.success) {
        return value;
    }
    const refunds = normalizedRootResult.data.refunds;
    if (!Array.isArray(refunds)) {
        return normalizedRootResult.data;
    }
    return {
        ...normalizedRootResult.data,
        refunds: refunds.map((refund: unknown): unknown => {
            const amountNormalizedRefund =
                normalizeStoredJsonSafeIntegerObjectFields(refund, {
                    domain: 'clover-transaction-details.refund.amount',
                    fields: CloverStoredTransactionDetailsAmountFields,
                    minimum: 0,
                    maximum: 999999999,
                });
            return normalizeStoredJsonSafeIntegerObjectFields(
                amountNormalizedRefund,
                {
                    domain: 'clover-transaction-details.refund.timestamp',
                    fields: CloverStoredTransactionDetailsTimestampFields,
                    minimum: 0,
                    maximum: Number.MAX_SAFE_INTEGER,
                }
            );
        }),
    };
}

export const StoredCloverTransactionDetailsPaymentBodySchema: z.ZodType<CloverTransactionDetailsPaymentBody> =
    z.preprocess(
        normalizeStoredCloverTransactionDetailsNumericFields,
        CloverTransactionDetailsPaymentBodySchema
    );

export const CloverCnpTokenSaleNetworkStatusSchema: z.ZodType<CloverCnpTokenSaleNetworkStatus> =
    z.enum(CloverCnpTokenSaleNetworkStatus);

export const CloverCnpTokenSaleOutcomeTypeSchema: z.ZodType<CloverCnpTokenSaleOutcomeType> =
    z.enum(CloverCnpTokenSaleOutcomeType);

export const CloverCnpTokenSaleStatusSchema: z.ZodType<CloverCnpTokenSaleStatus> =
    z.enum(CloverCnpTokenSaleStatus);

export const CloverCnpTokenSaleChargeOutcomeBodySchema: z.ZodObject<{
    network_status: z.ZodOptional<typeof CloverCnpTokenSaleNetworkStatusSchema>;
    reason: z.ZodOptional<z.ZodNullable<z.ZodString>>;
    seller_message: z.ZodOptional<z.ZodString>;
    type: z.ZodOptional<typeof CloverCnpTokenSaleOutcomeTypeSchema>;
}> = z.object({
    network_status: CloverCnpTokenSaleNetworkStatusSchema.optional(),
    reason: z.string().min(1).nullable().optional(),
    seller_message: z.string().min(1).optional(),
    type: CloverCnpTokenSaleOutcomeTypeSchema.optional(),
}) satisfies z.ZodType<CloverCnpTokenSaleChargeOutcomeBody>;

export const CloverCnpTokenSaleSourceBodySchema: z.ZodObject<{
    id: z.ZodOptional<z.ZodString>;
    object: z.ZodOptional<z.ZodString>;
    brand: z.ZodOptional<z.ZodNullable<z.ZodString>>;
    exp_month: z.ZodOptional<z.ZodString>;
    exp_year: z.ZodOptional<z.ZodString>;
    first6: z.ZodOptional<z.ZodString>;
    funding: z.ZodOptional<z.ZodString>;
    last4: z.ZodOptional<z.ZodString>;
    name: z.ZodOptional<z.ZodString>;
}> = z.object({
    id: z.string().min(1).optional(),
    object: z.string().min(1).optional(),
    brand: z.string().min(1).nullable().optional(),
    exp_month: z.string().min(1).optional(),
    exp_year: z.string().min(1).optional(),
    first6: z.string().min(1).optional(),
    funding: z.string().min(1).optional(),
    last4: z.string().min(1).optional(),
    name: z.string().min(1).optional(),
}) satisfies z.ZodType<CloverCnpTokenSaleSourceBody>;

export const CloverCnpTokenSaleChargeBodySchema: z.ZodObject<{
    id: z.ZodString;
    amount: typeof CloverCnpTokenSaleAmountSchema;
    amount_refunded: z.ZodOptional<z.ZodNumber>;
    auth_code: z.ZodOptional<z.ZodNullable<z.ZodString>>;
    captured: z.ZodBoolean;
    created: z.ZodOptional<z.ZodNumber>;
    currency: typeof CloverCnpTokenSaleCurrencySchema;
    description: z.ZodOptional<z.ZodNullable<z.ZodString>>;
    external_reference_id: z.ZodOptional<z.ZodString>;
    failure_code: z.ZodOptional<z.ZodNullable<z.ZodString>>;
    failure_message: z.ZodOptional<z.ZodNullable<z.ZodString>>;
    outcome: z.ZodOptional<typeof CloverCnpTokenSaleChargeOutcomeBodySchema>;
    paid: z.ZodBoolean;
    receipt_email: z.ZodOptional<z.ZodNullable<z.ZodString>>;
    ref_num: z.ZodOptional<z.ZodNullable<z.ZodString>>;
    refunded: z.ZodOptional<z.ZodBoolean>;
    source: z.ZodOptional<typeof CloverCnpTokenSaleSourceBodySchema>;
    status: typeof CloverCnpTokenSaleStatusSchema;
}> = z.object({
    id: z.string().min(1),
    amount: CloverCnpTokenSaleAmountSchema,
    amount_refunded: z.number().int().min(0).optional(),
    auth_code: z.string().min(1).nullable().optional(),
    captured: z.boolean(),
    created: z.number().int().min(0).optional(),
    currency: CloverCnpTokenSaleCurrencySchema,
    description: z.string().min(1).nullable().optional(),
    external_reference_id: z.string().min(1).max(12).optional(),
    failure_code: z.string().min(1).nullable().optional(),
    failure_message: z.string().min(1).nullable().optional(),
    outcome: CloverCnpTokenSaleChargeOutcomeBodySchema.optional(),
    paid: z.boolean(),
    receipt_email: z.string().min(1).nullable().optional(),
    ref_num: z.string().min(1).nullable().optional(),
    refunded: z.boolean().optional(),
    source: CloverCnpTokenSaleSourceBodySchema.optional(),
    status: CloverCnpTokenSaleStatusSchema,
}) satisfies z.ZodType<CloverCnpTokenSaleChargeBody>;

export const CloverRefundPaymentResultSchema: z.ZodType<CloverRefundPaymentResult> =
    z.enum(CloverRefundPaymentResult);

export const CloverRefundReferenceBodySchema: z.ZodObject<{
    id: z.ZodOptional<z.ZodString>;
}> = z.object({
    id: z.string().min(1).optional(),
}) satisfies z.ZodType<CloverRefundReferenceBody>;

export const CloverRefundCardTransactionBodySchema: z.ZodObject<{
    authCode: z.ZodOptional<z.ZodString>;
    referenceId: z.ZodOptional<z.ZodString>;
    state: z.ZodOptional<z.ZodString>;
    token: z.ZodOptional<z.ZodString>;
    transactionNo: z.ZodOptional<z.ZodString>;
    type: z.ZodOptional<z.ZodString>;
}> = z.object({
    authCode: z.string().min(1).optional(),
    referenceId: z.string().min(1).optional(),
    state: z.string().min(1).optional(),
    token: z.string().min(1).optional(),
    transactionNo: z.string().min(1).optional(),
    type: z.string().min(1).optional(),
}) satisfies z.ZodType<CloverRefundCardTransactionBody>;

export const CloverRefundBodySchema: z.ZodType<CloverRefundBody> = z.object({
    id: z.string().min(1).optional(),
    amount: CloverRefundAmountSchema.optional(),
    cardTransaction: CloverRefundCardTransactionBodySchema.optional(),
    createdTime: z.number().int().min(0).optional(),
    externalReferenceId: z.string().min(1).max(12).optional(),
    payment: CloverRefundReferenceBodySchema.optional(),
    taxAmount: z.number().int().min(0).optional(),
    tipAmount: z.number().int().min(0).optional(),
    voided: z.boolean().optional(),
});

export const CloverRefundPaymentBodySchema: z.ZodObject<{
    id: z.ZodOptional<z.ZodString>;
    amount: z.ZodOptional<typeof CloverRefundAmountSchema>;
    result: z.ZodOptional<typeof CloverRefundPaymentResultSchema>;
    cardTransaction: z.ZodOptional<
        typeof CloverRefundCardTransactionBodySchema
    >;
    externalPaymentId: z.ZodOptional<z.ZodString>;
    externalReferenceId: z.ZodOptional<z.ZodString>;
    refunds: z.ZodOptional<z.ZodArray<typeof CloverRefundBodySchema>>;
}> = z.object({
    id: z.string().min(1).max(13).optional(),
    amount: CloverRefundAmountSchema.optional(),
    result: CloverRefundPaymentResultSchema.optional(),
    cardTransaction: CloverRefundCardTransactionBodySchema.optional(),
    externalPaymentId: z.string().min(1).max(32).optional(),
    externalReferenceId: z.string().min(1).max(12).optional(),
    refunds: z.array(CloverRefundBodySchema).optional(),
}) satisfies z.ZodType<CloverRefundPaymentBody>;

export const CloverRefundResponseBodySchema: z.ZodObject<{
    payment: z.ZodOptional<typeof CloverRefundPaymentBodySchema>;
    paymentId: z.ZodString;
    refund: z.ZodOptional<typeof CloverRefundBodySchema>;
}> = z.object({
    payment: CloverRefundPaymentBodySchema.optional(),
    paymentId: z.string().min(1),
    refund: CloverRefundBodySchema.optional(),
}) satisfies z.ZodType<CloverRefundResponseBody>;

export const CloverRefundErrorBodySchema: z.ZodObject<{
    message: z.ZodString;
    type: z.ZodString;
    code: z.ZodOptional<z.ZodString>;
    requestId: z.ZodOptional<z.ZodString>;
    requestType: z.ZodOptional<z.ZodString>;
}> = z.object({
    message: z.string().min(1),
    type: z.string().min(1),
    code: z.string().min(1).optional(),
    requestId: z.string().min(1).optional(),
    requestType: z.string().min(1).optional(),
}) satisfies z.ZodType<CloverRefundErrorBody>;

export const CloverCloudPayRefundSuccessfulResponseDetailsSchema: z.ZodObject<{
    kind: z.ZodLiteral<typeof CloverRefundKind.CLOUD_PAY>;
    outcome: z.ZodLiteral<typeof CloverRefundOutcome.REFUNDED>;
    httpStatus: z.ZodLiteral<200>;
    body: typeof CloverRefundResponseBodySchema;
}> = z.object({
    kind: z.literal(CloverRefundKind.CLOUD_PAY),
    outcome: z.literal(CloverRefundOutcome.REFUNDED),
    httpStatus: z.literal(200),
    body: CloverRefundResponseBodySchema,
}) satisfies z.ZodType<CloverCloudPayRefundSuccessfulResponseDetails>;

export const CloverEcommerceRefundResponseBodySchema = z
    .object({
        id: z.string().min(1),
        object: z.literal('refund'),
        amount: CloverRefundAmountSchema,
        charge: z.string().min(1),
        created: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
        status: z.literal('succeeded'),
        metadata: z
            .object({
                refundType: z.string().min(1).optional(),
                authCode: z.string().min(1).optional(),
                refNum: z.string().min(1).optional(),
                gatewayResponseCode: z.string().min(1).optional(),
            })
            .strict()
            .optional(),
    })
    .strict() satisfies z.ZodType<CloverEcommerceRefundResponseBody>;

export const CloverEcommerceRefundSuccessfulResponseDetailsSchema = z
    .object({
        kind: z.literal(CloverRefundKind.ECOMMERCE),
        outcome: z.literal(CloverRefundOutcome.REFUNDED),
        httpStatus: z.literal(200),
        body: CloverEcommerceRefundResponseBodySchema,
    })
    .strict() satisfies z.ZodType<CloverEcommerceRefundSuccessfulResponseDetails>;

export const CloverRefundSuccessfulResponseDetailsSchema: z.ZodType<CloverRefundSuccessfulResponseDetails> =
    z.discriminatedUnion('kind', [
        CloverCloudPayRefundSuccessfulResponseDetailsSchema,
        CloverEcommerceRefundSuccessfulResponseDetailsSchema,
    ]);

export const CloverRefundRejectedResponseDetailsSchema: z.ZodObject<{
    kind: z.ZodEnum<typeof CloverRefundKind>;
    outcome: z.ZodLiteral<typeof CloverRefundOutcome.REJECTED>;
    httpStatus: z.ZodNumber;
    body: typeof CloverRefundErrorBodySchema;
}> = z.object({
    kind: z.enum(CloverRefundKind),
    outcome: z.literal(CloverRefundOutcome.REJECTED),
    httpStatus: z.number().int().positive(),
    body: CloverRefundErrorBodySchema,
}) satisfies z.ZodType<CloverRefundRejectedResponseDetails>;

export const CloverRefundMalformedResponseDetailsSchema: z.ZodObject<{
    kind: z.ZodEnum<typeof CloverRefundKind>;
    outcome: z.ZodLiteral<typeof CloverRefundOutcome.MALFORMED_RESPONSE>;
    httpStatus: z.ZodNumber;
    body: z.ZodUnknown;
    message: z.ZodString;
}> = z.object({
    kind: z.enum(CloverRefundKind),
    outcome: z.literal(CloverRefundOutcome.MALFORMED_RESPONSE),
    httpStatus: z.number().int().positive(),
    body: z.unknown(),
    message: z.string().min(1),
}) satisfies z.ZodType<CloverRefundMalformedResponseDetails>;

export const CloverRefundResponseDetailsSchema: z.ZodType<CloverRefundResponseDetails> =
    z.union([
        CloverRefundSuccessfulResponseDetailsSchema,
        CloverRefundRejectedResponseDetailsSchema,
        CloverRefundMalformedResponseDetailsSchema,
    ]);

export const CloverVoidStatusSchema: z.ZodType<CloverVoidStatus> =
    z.enum(CloverVoidStatus);

export const CloverVoidPaymentResultSchema: z.ZodType<CloverVoidPaymentResult> =
    z.enum(CloverVoidPaymentResult);

export const CloverVoidReferenceBodySchema: z.ZodObject<{
    id: z.ZodOptional<z.ZodString>;
}> = z.object({
    id: z.string().min(1).max(13).optional(),
}) satisfies z.ZodType<CloverVoidReferenceBody>;

export const CloverVoidCardTransactionBodySchema: z.ZodObject<{
    authCode: z.ZodOptional<z.ZodString>;
    referenceId: z.ZodOptional<z.ZodString>;
    state: z.ZodOptional<z.ZodString>;
    token: z.ZodOptional<z.ZodString>;
    transactionNo: z.ZodOptional<z.ZodString>;
    type: z.ZodOptional<z.ZodString>;
}> = z.object({
    authCode: z.string().min(1).optional(),
    referenceId: z.string().min(1).optional(),
    state: z.string().min(1).optional(),
    token: z.string().min(1).optional(),
    transactionNo: z.string().min(1).optional(),
    type: z.string().min(1).optional(),
}) satisfies z.ZodType<CloverVoidCardTransactionBody>;

export const CloverVoidPaymentBodySchema: z.ZodObject<{
    id: z.ZodOptional<z.ZodString>;
    amount: z.ZodOptional<z.ZodNumber>;
    result: z.ZodOptional<typeof CloverVoidPaymentResultSchema>;
    cardTransaction: z.ZodOptional<typeof CloverVoidCardTransactionBodySchema>;
    externalPaymentId: z.ZodOptional<z.ZodString>;
    externalReferenceId: z.ZodOptional<z.ZodString>;
    refunds: z.ZodOptional<z.ZodArray<z.ZodUnknown>>;
    voidPaymentRef: z.ZodOptional<typeof CloverVoidReferenceBodySchema>;
    voidReason: z.ZodOptional<typeof CloverVoidReasonSchema>;
}> = z.object({
    id: z.string().min(1).max(13).optional(),
    amount: z.number().int().min(1).max(999999999).optional(),
    result: CloverVoidPaymentResultSchema.optional(),
    cardTransaction: CloverVoidCardTransactionBodySchema.optional(),
    externalPaymentId: z.string().min(1).max(32).optional(),
    externalReferenceId: z.string().min(1).max(12).optional(),
    refunds: z.array(z.unknown()).optional(),
    voidPaymentRef: CloverVoidReferenceBodySchema.optional(),
    voidReason: CloverVoidReasonSchema.optional(),
}) satisfies z.ZodType<CloverVoidPaymentBody>;

export const CloverCloudPayVoidResponseBodySchema: z.ZodObject<{
    payment: z.ZodOptional<typeof CloverVoidPaymentBodySchema>;
    paymentId: z.ZodString;
    voidReason: z.ZodOptional<typeof CloverVoidReasonSchema>;
    voidStatus: z.ZodOptional<typeof CloverVoidStatusSchema>;
}> = z.object({
    payment: CloverVoidPaymentBodySchema.optional(),
    paymentId: z.string().min(13).max(13),
    voidReason: CloverVoidReasonSchema.optional(),
    voidStatus: CloverVoidStatusSchema.optional(),
}) satisfies z.ZodType<CloverCloudPayVoidResponseBody>;

export const CloverEcommerceVoidResponseBodySchema: z.ZodType<CloverEcommerceVoidResponseBody> =
    z.object({
        id: z.string().min(1),
        object: z.literal('refund'),
        amount: z.number().int().min(1),
        charge: z.string().min(1),
        created: z.number().int().min(0).optional(),
        status: z.literal('succeeded'),
        metadata: z
            .object({
                refundType: z.string().min(1).optional(),
                authCode: z.string().min(1).optional(),
                refNum: z.string().min(1).optional(),
                gatewayResponseCode: z.string().min(1).optional(),
            })
            .optional(),
    });

export const CloverVoidResponseBodySchema: z.ZodType<CloverVoidResponseBody> = z.union([
    CloverCloudPayVoidResponseBodySchema,
    CloverEcommerceVoidResponseBodySchema,
]);

export const CloverVoidErrorBodySchema: z.ZodObject<{
    message: z.ZodString;
    type: z.ZodString;
    code: z.ZodOptional<z.ZodString>;
    requestId: z.ZodOptional<z.ZodString>;
    requestType: z.ZodOptional<z.ZodString>;
}> = z.object({
    message: z.string().min(1),
    type: z.string().min(1),
    code: z.string().min(1).optional(),
    requestId: z.string().min(1).optional(),
    requestType: z.string().min(1).optional(),
}) satisfies z.ZodType<CloverVoidErrorBody>;

export const CloverVoidSuccessfulResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodEnum<{
        readonly VOIDED: typeof CloverVoidOutcome.VOIDED;
        readonly REFUNDED: typeof CloverVoidOutcome.REFUNDED;
    }>;
    httpStatus: z.ZodLiteral<200>;
    body: typeof CloverVoidResponseBodySchema;
}> = z.object({
    outcome: z.enum({
        VOIDED: CloverVoidOutcome.VOIDED,
        REFUNDED: CloverVoidOutcome.REFUNDED,
    }),
    httpStatus: z.literal(200),
    body: CloverVoidResponseBodySchema,
}) satisfies z.ZodType<CloverVoidSuccessfulResponseDetails>;

export const CloverVoidRejectedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverVoidOutcome.REJECTED>;
    httpStatus: z.ZodNumber;
    body: typeof CloverVoidErrorBodySchema;
}> = z.object({
    outcome: z.literal(CloverVoidOutcome.REJECTED),
    httpStatus: z.number().int().positive(),
    body: CloverVoidErrorBodySchema,
}) satisfies z.ZodType<CloverVoidRejectedResponseDetails>;

export const CloverVoidMalformedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverVoidOutcome.MALFORMED_RESPONSE>;
    httpStatus: z.ZodNumber;
    body: z.ZodUnknown;
    message: z.ZodString;
}> = z.object({
    outcome: z.literal(CloverVoidOutcome.MALFORMED_RESPONSE),
    httpStatus: z.number().int().positive(),
    body: z.unknown(),
    message: z.string().min(1),
}) satisfies z.ZodType<CloverVoidMalformedResponseDetails>;

export const CloverVoidResponseDetailsSchema: z.ZodType<CloverVoidResponseDetails> =
    z.discriminatedUnion('outcome', [
        CloverVoidSuccessfulResponseDetailsSchema,
        CloverVoidRejectedResponseDetailsSchema,
        CloverVoidMalformedResponseDetailsSchema,
    ]);

export const CloverLifecyclePreflightNormalizedResultSchema: z.ZodType<CloverLifecyclePreflightNormalizedResult> =
    z.object({
        gateway_operation: z.literal(
            CloverLifecyclePreflightGatewayOperation.TERMINAL_SALE
        ),
        device_id: z.string().min(1),
        amount: z.number().int().min(1).max(CloverMinorUnitMaximum),
        external_payment_id: z.string().min(1),
        final: z.literal(true),
        capture: z.literal(true).optional(),
        external_reference_id: z.string().min(1).max(12).optional(),
        tip_amount: z.number().int().min(0).max(CloverMinorUnitMaximum).optional(),
    });

export const CloverLifecyclePreflightRecordedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverLifecyclePreflightOutcome.RECORDED>;
    normalized_result: typeof CloverLifecyclePreflightNormalizedResultSchema;
}> = z.object({
    outcome: z.literal(CloverLifecyclePreflightOutcome.RECORDED),
    normalized_result: CloverLifecyclePreflightNormalizedResultSchema,
}) satisfies z.ZodType<
    Extract<
        CloverLifecyclePreflightResponseDetails,
        {
            readonly outcome: typeof CloverLifecyclePreflightOutcome.RECORDED;
        }
    >
>;

export const CloverLifecyclePreflightRejectedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverLifecyclePreflightOutcome.REJECTED>;
    message: z.ZodString;
}> = z.object({
    outcome: z.literal(CloverLifecyclePreflightOutcome.REJECTED),
    message: z.string().min(1),
}) satisfies z.ZodType<
    Extract<
        CloverLifecyclePreflightResponseDetails,
        {
            readonly outcome: typeof CloverLifecyclePreflightOutcome.REJECTED;
        }
    >
>;

export const CloverLifecyclePreflightMalformedInputResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<
        typeof CloverLifecyclePreflightOutcome.MALFORMED_INPUT
    >;
    message: z.ZodString;
    body: z.ZodUnknown;
}> = z.object({
    outcome: z.literal(CloverLifecyclePreflightOutcome.MALFORMED_INPUT),
    message: z.string().min(1),
    body: z.unknown(),
}) satisfies z.ZodType<
    Extract<
        CloverLifecyclePreflightResponseDetails,
        {
            readonly outcome: typeof CloverLifecyclePreflightOutcome.MALFORMED_INPUT;
        }
    >
>;

export const CloverLifecyclePreflightResponseDetailsSchema: z.ZodType<CloverLifecyclePreflightResponseDetails> =
    z.discriminatedUnion('outcome', [
        CloverLifecyclePreflightRecordedResponseDetailsSchema,
        CloverLifecyclePreflightRejectedResponseDetailsSchema,
        CloverLifecyclePreflightMalformedInputResponseDetailsSchema,
    ]);

export const CloverLifecyclePreflightResponsePayloadSchema: z.ZodObject<{
    gateway_lifecycle_payload_type: z.ZodLiteral<
        typeof GatewayLifecycleResponseType.CLOVER_LIFECYCLE_PREFLIGHT
    >;
    response_details: typeof CloverLifecyclePreflightResponseDetailsSchema;
}> = z.object({
    gateway_lifecycle_payload_type: z.literal(
        GatewayLifecycleResponseType.CLOVER_LIFECYCLE_PREFLIGHT
    ),
    response_details: CloverLifecyclePreflightResponseDetailsSchema,
}) satisfies z.ZodType<CloverLifecyclePreflightResponsePayload>;

export const CloverLifecycleFinalizationTerminalSaleStatusSchema: z.ZodType<
    CloverLifecycleFinalizationNormalizedResult['status']
> = z.enum([
    PaymentGatewayTerminalSaleStatus.APPROVED,
    PaymentGatewayTerminalSaleStatus.DECLINED,
    PaymentGatewayTerminalSaleStatus.CANCELED,
    PaymentGatewayTerminalSaleStatus.MALFORMED_RESPONSE,
]);

export const CloverLifecycleFinalizationNormalizedResultSchema: z.ZodType<CloverLifecycleFinalizationNormalizedResult> =
    z.object({
        gateway_operation: z.literal(
            CloverLifecycleFinalizationGatewayOperation.TERMINAL_SALE
        ),
        status: CloverLifecycleFinalizationTerminalSaleStatusSchema,
        gateway_transaction_id: z.string().min(1).optional(),
        approved_amount: z.string().min(1).optional(),
        authorization_code: z.string().min(1).optional(),
        gateway_token: z.string().min(1).optional(),
        message: z.string().min(1).optional(),
        response_code: z.string().min(1).optional(),
    });

export const CloverLifecycleFinalizationFinalizedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverLifecycleFinalizationOutcome.FINALIZED>;
    normalized_result: typeof CloverLifecycleFinalizationNormalizedResultSchema;
}> = z.object({
    outcome: z.literal(CloverLifecycleFinalizationOutcome.FINALIZED),
    normalized_result: CloverLifecycleFinalizationNormalizedResultSchema,
}) satisfies z.ZodType<
    Extract<
        CloverLifecycleFinalizationResponseDetails,
        {
            readonly outcome: typeof CloverLifecycleFinalizationOutcome.FINALIZED;
        }
    >
>;

export const CloverLifecycleFinalizationMalformedResultResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<
        typeof CloverLifecycleFinalizationOutcome.MALFORMED_RESULT
    >;
    message: z.ZodString;
    body: z.ZodUnknown;
}> = z.object({
    outcome: z.literal(CloverLifecycleFinalizationOutcome.MALFORMED_RESULT),
    message: z.string().min(1),
    body: z.unknown(),
}) satisfies z.ZodType<
    Extract<
        CloverLifecycleFinalizationResponseDetails,
        {
            readonly outcome: typeof CloverLifecycleFinalizationOutcome.MALFORMED_RESULT;
        }
    >
>;

export const CloverLifecycleFinalizationResponseDetailsSchema: z.ZodType<CloverLifecycleFinalizationResponseDetails> =
    z.discriminatedUnion('outcome', [
        CloverLifecycleFinalizationFinalizedResponseDetailsSchema,
        CloverLifecycleFinalizationMalformedResultResponseDetailsSchema,
    ]);

export const CloverLifecycleFinalizationResponsePayloadSchema: z.ZodObject<{
    gateway_lifecycle_payload_type: z.ZodLiteral<
        typeof GatewayLifecycleResponseType.CLOVER_LIFECYCLE_FINALIZATION
    >;
    response_details: typeof CloverLifecycleFinalizationResponseDetailsSchema;
}> = z.object({
    gateway_lifecycle_payload_type: z.literal(
        GatewayLifecycleResponseType.CLOVER_LIFECYCLE_FINALIZATION
    ),
    response_details: CloverLifecycleFinalizationResponseDetailsSchema,
}) satisfies z.ZodType<CloverLifecycleFinalizationResponsePayload>;

function normalizeStoredNumberOrStringFields(value: unknown, fields: readonly string[]): unknown {
    const recordResult = StoredJsonNumberRecordSchema.safeParse(value);
    if (!recordResult.success) {
        return value;
    }
    const normalized: Record<string, unknown> = { ...recordResult.data };
    for (const field of fields) {
        const numberResult = StoredJsonNumberSchema.safeParse(recordResult.data[field]);
        if (numberResult.success) {
            normalized[field] = extractStoredJsonNumberToken(numberResult.data);
        }
    }
    return normalized;
}

function normalizeStoredValorTaxComponent(value: unknown): unknown {
    const normalizedRate = normalizeStoredJsonNumberFieldsAtPath(value, {
        domain: 'gateway-lifecycle-response.valor-txnfetch.tax-rate',
        numericKind: 'bounded-binary64',
        path: [],
        fields: ['rate'],
        minimum: -LifecycleNumericMagnitudeMaximum,
        maximum: LifecycleNumericMagnitudeMaximum,
    });
    return normalizeStoredNumberOrStringFields(normalizedRate, ['amount', 'taxableSales']);
}

function normalizeStoredValorTxnFetchRecord(value: unknown): unknown {
    const normalizedRecord = normalizeStoredNumberOrStringFields(value, ['TOTAL_TAX']);
    const recordResult = StoredJsonNumberRecordSchema.safeParse(normalizedRecord);
    const taxDetailsResult = StoredJsonNumberRecordSchema.safeParse(
        recordResult.success ? recordResult.data.TAX_DETAILS : undefined
    );
    if (!recordResult.success || !taxDetailsResult.success) {
        return normalizedRecord;
    }
    const normalizedTaxDetails: Record<string, unknown> = {
        ...taxDetailsResult.data,
    };
    for (const field of ['stateTax', 'cityTax', 'reducedTax']) {
        if (Object.hasOwn(taxDetailsResult.data, field)) {
            normalizedTaxDetails[field] = normalizeStoredValorTaxComponent(
                taxDetailsResult.data[field]
            );
        }
    }
    return {
        ...recordResult.data,
        TAX_DETAILS: normalizedTaxDetails,
    };
}

function normalizeStoredValorTxnFetchResponse(value: unknown): unknown {
    const normalizedCount = normalizeStoredJsonSafeIntegerFieldsAtPath(value, {
        domain: 'gateway-lifecycle-response.valor-txnfetch.count',
        path: [],
        fields: ['number_of_records'],
        minimum: 0,
        maximum: Number.MAX_SAFE_INTEGER,
    });
    const rootResult = StoredJsonNumberRecordSchema.safeParse(normalizedCount);
    if (!rootResult.success || !Array.isArray(rootResult.data.data)) {
        return normalizedCount;
    }
    return {
        ...rootResult.data,
        data: rootResult.data.data.map(normalizeStoredValorTxnFetchRecord),
    };
}

export const StoredGatewayResponseJsonValueRuntimeSchema: z.ZodType<unknown> = z.lazy(() =>
    z.union([
        z.string(),
        z.boolean(),
        z.null(),
        StoredJsonNumberSchema,
        z.array(StoredGatewayResponseJsonValueRuntimeSchema),
        z.record(z.string(), StoredGatewayResponseJsonValueRuntimeSchema),
    ])
);

export const StoredGatewayResponseJsonObjectRuntimeSchema: z.ZodType<Record<string, unknown>> =
    z.record(z.string(), StoredGatewayResponseJsonValueRuntimeSchema);

export const StoredProviderGatewayResponseSnapshotSchema: z.ZodType<ProviderGatewayResponseSnapshot> =
    z.custom<ProviderGatewayResponseSnapshot>(
        (value: unknown): value is ProviderGatewayResponseSnapshot =>
            StoredGatewayResponseJsonObjectRuntimeSchema.safeParse(value).success,
        { message: 'Invalid stored provider gateway response snapshot' }
    );

function normalizeStoredCloverChargeResponse(value: unknown): unknown {
    const normalizedAmounts = normalizeStoredJsonSafeIntegerFieldsAtPath(value, {
        domain: 'gateway-lifecycle-response.clover-charge.amount',
        path: [],
        fields: ['amount', 'amount_refunded'],
        minimum: 0,
        maximum: CloverMinorUnitMaximum,
    });
    return normalizeStoredJsonSafeIntegerFieldsAtPath(normalizedAmounts, {
        domain: 'gateway-lifecycle-response.clover-charge.created',
        path: [],
        fields: ['created'],
        minimum: 0,
        maximum: Number.MAX_SAFE_INTEGER,
    });
}

function normalizeStoredCloverPreflightResponse(value: unknown): unknown {
    return normalizeStoredJsonSafeIntegerFieldsAtPath(value, {
        domain: 'gateway-lifecycle-response.clover-preflight.result',
        path: ['response_details', 'normalized_result'],
        fields: ['amount', 'tip_amount'],
        minimum: 0,
        maximum: CloverMinorUnitMaximum,
    });
}

function normalizeStoredCloverTypedResponseDetails(value: unknown, domain: string): unknown {
    return normalizeStoredJsonSafeIntegerFieldsAtPath(value, {
        domain: `${domain}.http-status`,
        path: [],
        fields: ['httpStatus'],
        minimum: HttpStatusMinimum,
        maximum: HttpStatusMaximum,
    });
}

function normalizeStoredCloverTerminalSaleResponse(value: unknown): unknown {
    const normalizedStatus = normalizeStoredJsonSafeIntegerFieldsAtPath(value, {
        domain: 'gateway-lifecycle-response.clover-terminal-sale.http-status',
        path: ['response_details'],
        fields: ['httpStatus'],
        minimum: HttpStatusMinimum,
        maximum: HttpStatusMaximum,
    });
    const rootResult = StoredJsonNumberRecordSchema.safeParse(normalizedStatus);
    const responseDetailsResult = StoredJsonNumberRecordSchema.safeParse(
        rootResult.success ? rootResult.data.response_details : undefined
    );
    if (
        !responseDetailsResult.success ||
        (responseDetailsResult.data.outcome !== 'approved' &&
            responseDetailsResult.data.outcome !== 'declined')
    ) {
        return normalizedStatus;
    }
    return normalizeStoredJsonSafeIntegerFieldsAtPath(normalizedStatus, {
        domain: 'gateway-lifecycle-response.clover-terminal-sale.payment',
        path: ['response_details', 'body', 'payment'],
        fields: ['amount'],
        minimum: 1,
        maximum: CloverMinorUnitMaximum,
    });
}

function normalizeStoredCloverRefundBody(value: unknown): unknown {
    const normalized = normalizeStoredJsonSafeIntegerFieldsAtPath(value, {
        domain: 'gateway-lifecycle-response.clover-refund.body',
        path: [],
        fields: ['amount', 'taxAmount', 'tipAmount'],
        minimum: 0,
        maximum: CloverMinorUnitMaximum,
    });
    return normalizeStoredJsonSafeIntegerFieldsAtPath(normalized, {
        domain: 'gateway-lifecycle-response.clover-refund.created',
        path: [],
        fields: ['created', 'createdTime'],
        minimum: 0,
        maximum: Number.MAX_SAFE_INTEGER,
    });
}

function normalizeStoredCloverRefundResponse(value: unknown): unknown {
    const normalizedStatusWithoutKind = normalizeStoredCloverTypedResponseDetails(
        value,
        'gateway-lifecycle-response.clover-refund'
    );
    const normalizedStatusResult = StoredJsonNumberRecordSchema.safeParse(
        normalizedStatusWithoutKind
    );
    const normalizedStatus =
        normalizedStatusResult.success && normalizedStatusResult.data.kind === undefined
            ? {
                  ...normalizedStatusResult.data,
                  kind: CloverRefundKind.CLOUD_PAY,
              }
            : normalizedStatusWithoutKind;
    const rootResult = StoredJsonNumberRecordSchema.safeParse(normalizedStatus);
    if (!rootResult.success || rootResult.data.outcome !== 'refunded') {
        return normalizedStatus;
    }
    const bodyResult = StoredJsonNumberRecordSchema.safeParse(rootResult.data.body);
    if (!bodyResult.success) {
        return normalizedStatus;
    }
    if (rootResult.data.kind === CloverRefundKind.ECOMMERCE) {
        return {
            ...rootResult.data,
            body: normalizeStoredCloverRefundBody(bodyResult.data),
        };
    }

    const paymentResult = StoredJsonNumberRecordSchema.safeParse(bodyResult.data.payment);
    const normalizedPaymentResult = StoredJsonNumberRecordSchema.safeParse(
        paymentResult.success ? normalizeStoredCloverRefundBody(paymentResult.data) : undefined
    );
    const normalizedPayment =
        paymentResult.success && normalizedPaymentResult.success
            ? {
                  ...normalizedPaymentResult.data,
                  refunds: Array.isArray(paymentResult.data.refunds)
                      ? paymentResult.data.refunds.map((refund: unknown): unknown =>
                            normalizeStoredCloverRefundBody(refund)
                        )
                      : paymentResult.data.refunds,
              }
            : bodyResult.data.payment;

    return {
        ...rootResult.data,
        body: {
            ...bodyResult.data,
            payment: normalizedPayment,
            refund: normalizeStoredCloverRefundBody(bodyResult.data.refund),
        },
    };
}

function normalizeStoredCloverVoidResponse(value: unknown): unknown {
    const normalizedStatus = normalizeStoredCloverTypedResponseDetails(
        value,
        'gateway-lifecycle-response.clover-void'
    );
    const rootResult = StoredJsonNumberRecordSchema.safeParse(normalizedStatus);
    if (
        !rootResult.success ||
        (rootResult.data.outcome !== 'voided' && rootResult.data.outcome !== 'refunded')
    ) {
        return normalizedStatus;
    }
    const bodyResult = StoredJsonNumberRecordSchema.safeParse(rootResult.data.body);
    if (bodyResult.success && bodyResult.data.object === 'refund') {
        const normalizedAmount = normalizeStoredJsonSafeIntegerFieldsAtPath(normalizedStatus, {
            domain: 'gateway-lifecycle-response.clover-void.refund',
            path: ['body'],
            fields: ['amount'],
            minimum: 0,
            maximum: CloverMinorUnitMaximum,
        });
        return normalizeStoredJsonSafeIntegerFieldsAtPath(normalizedAmount, {
            domain: 'gateway-lifecycle-response.clover-void.refund-created',
            path: ['body'],
            fields: ['created'],
            minimum: 0,
            maximum: Number.MAX_SAFE_INTEGER,
        });
    }
    return normalizeStoredJsonSafeIntegerFieldsAtPath(normalizedStatus, {
        domain: 'gateway-lifecycle-response.clover-void.payment',
        path: ['body', 'payment'],
        fields: ['amount'],
        minimum: 1,
        maximum: CloverMinorUnitMaximum,
    });
}

function normalizeStoredCloverGatewayResponsePayload(
    responseType: unknown,
    payload: unknown
): unknown {
    switch (responseType) {
        case GatewayLifecycleResponseType.CLOVER_CHARGE:
            return normalizeStoredCloverChargeResponse(payload);
        case GatewayLifecycleResponseType.CLOVER_LIFECYCLE_PREFLIGHT:
            return normalizeStoredCloverPreflightResponse(payload);
        case GatewayLifecycleResponseType.CLOVER_LIFECYCLE_FINALIZATION:
        case GatewayLifecycleResponseType.CLOVER_CHARGE_HTTP_ERROR:
        case GatewayLifecycleResponseType.CLOVER_PAYMENT_EXTERNAL_HTTP_ERROR:
        case GatewayLifecycleResponseType.CLOVER_CHARGE_INVALID_JSON:
            return payload;
        case GatewayLifecycleResponseType.CLOVER_PAYMENT:
        case GatewayLifecycleResponseType.CLOVER_PAYMENT_EXTERNAL: {
            const paymentResult =
                StoredCloverTransactionDetailsPaymentBodySchema.safeParse(payload);
            return paymentResult.success ? paymentResult.data : payload;
        }
        case GatewayLifecycleResponseType.CLOVER_REFUND:
            return normalizeStoredCloverRefundResponse(payload);
        case GatewayLifecycleResponseType.CLOVER_TERMINAL_SALE:
            return normalizeStoredCloverTerminalSaleResponse(payload);
        case GatewayLifecycleResponseType.CLOVER_VOID:
            return normalizeStoredCloverVoidResponse(payload);
        default:
            return payload;
    }
}

export function normalizeStoredGatewayLifecycleResponseNumericLeaves(value: unknown): unknown {
    const rootResult = StoredJsonNumberRecordSchema.safeParse(value);
    if (!rootResult.success) {
        return value;
    }
    const normalizedPayload =
        rootResult.data.gateway_response_type === GatewayLifecycleResponseType.VALOR_TXN_FETCH
            ? normalizeStoredValorTxnFetchResponse(rootResult.data.gateway_response_json)
            : normalizeStoredCloverGatewayResponsePayload(
                  rootResult.data.gateway_response_type,
                  rootResult.data.gateway_response_json
              );
    if (normalizedPayload === rootResult.data.gateway_response_json) {
        return value;
    }
    return {
        ...rootResult.data,
        gateway_response_json: normalizedPayload,
    };
}

export const StoredCloverChargeResponseSchema: z.ZodType<
    z.output<typeof CloverCnpTokenSaleChargeBodySchema>
> = z.preprocess(normalizeStoredCloverChargeResponse, CloverCnpTokenSaleChargeBodySchema);

export const StoredCloverLifecyclePreflightResponseSchema: z.ZodType<
    z.output<typeof CloverLifecyclePreflightResponsePayloadSchema>
> = z.preprocess(
    normalizeStoredCloverPreflightResponse,
    CloverLifecyclePreflightResponsePayloadSchema
);

export const StoredCloverLifecycleFinalizationResponseSchema: z.ZodType<
    z.output<typeof CloverLifecycleFinalizationResponsePayloadSchema>
> = CloverLifecycleFinalizationResponsePayloadSchema;

export const StoredCloverTerminalSaleResponseSchema: z.ZodType<
    z.output<typeof CloverTerminalSaleResponseLifecyclePayloadSchema>
> = z.preprocess(
    normalizeStoredCloverTerminalSaleResponse,
    CloverTerminalSaleResponseLifecyclePayloadSchema
);

export const StoredCloverRefundResponseSchema: z.ZodType<
    z.output<typeof CloverRefundResponseDetailsSchema>
> = z.preprocess(normalizeStoredCloverRefundResponse, CloverRefundResponseDetailsSchema);

export const StoredCloverVoidResponseSchema: z.ZodType<
    z.output<typeof CloverVoidResponseDetailsSchema>
> = z.preprocess(normalizeStoredCloverVoidResponse, CloverVoidResponseDetailsSchema);

type GatewayLifecycleResponseVariantSchema<
    TResponseType extends GatewayLifecycleResponseType,
    TGatewayResponseJsonSchema extends z.ZodType,
> = z.ZodObject<{
    gateway_response_type: z.ZodLiteral<TResponseType>;
    gateway_response_json: TGatewayResponseJsonSchema;
}>;

function gatewayLifecycleResponseVariantSchema<
    TResponseType extends GatewayLifecycleResponseType,
    TGatewayResponseJsonSchema extends z.ZodType,
>(
    gatewayResponseType: TResponseType,
    gatewayResponseJsonSchema: TGatewayResponseJsonSchema
): GatewayLifecycleResponseVariantSchema<TResponseType, TGatewayResponseJsonSchema> {
    return z.object({
        gateway_response_type: z.literal(gatewayResponseType),
        gateway_response_json: gatewayResponseJsonSchema,
    });
}

export const GatewayLifecycleResponseTypeSchema: z.ZodType<GatewayLifecycleResponseType> =
    z.union([
        z.literal(GatewayLifecycleResponseType.CARDPOINTE_AUTH),
        z.literal(GatewayLifecycleResponseType.CARDPOINTE_AUTH_HTTP_ERROR),
        z.literal(GatewayLifecycleResponseType.CARDPOINTE_AUTH_INVALID_JSON),
        z.literal(GatewayLifecycleResponseType.CARDPOINTE_FUNDING),
        z.literal(GatewayLifecycleResponseType.CARDPOINTE_INQUIRE),
        z.literal(GatewayLifecycleResponseType.CARDPOINTE_INQUIRE_BY_ORDER_ID),
        z.literal(GatewayLifecycleResponseType.CARDPOINTE_LIFECYCLE_FINALIZATION),
        z.literal(GatewayLifecycleResponseType.CARDPOINTE_LIFECYCLE_PREFLIGHT),
        z.literal(GatewayLifecycleResponseType.CARDPOINTE_REFUND),
        z.literal(GatewayLifecycleResponseType.CARDPOINTE_TERMINAL_SALE),
        z.literal(GatewayLifecycleResponseType.CARDPOINTE_VOID),
        z.literal(GatewayLifecycleResponseType.CLOVER_CHARGE),
        z.literal(GatewayLifecycleResponseType.CLOVER_CHARGE_HTTP_ERROR),
        z.literal(GatewayLifecycleResponseType.CLOVER_CHARGE_INVALID_JSON),
        z.literal(GatewayLifecycleResponseType.CLOVER_LIFECYCLE_FINALIZATION),
        z.literal(GatewayLifecycleResponseType.CLOVER_LIFECYCLE_PREFLIGHT),
        z.literal(GatewayLifecycleResponseType.CLOVER_PAYMENT),
        z.literal(GatewayLifecycleResponseType.CLOVER_PAYMENT_EXTERNAL),
        z.literal(GatewayLifecycleResponseType.CLOVER_PAYMENT_EXTERNAL_HTTP_ERROR),
        z.literal(GatewayLifecycleResponseType.CLOVER_WEBHOOK_PAYMENT_RETRIEVAL),
        z.literal(GatewayLifecycleResponseType.CLOVER_REFUND),
        z.literal(GatewayLifecycleResponseType.CLOVER_TERMINAL_SALE),
        z.literal(GatewayLifecycleResponseType.CLOVER_VOID),
        z.literal(GatewayLifecycleResponseType.GATEWAY_TERMINAL_SALE),
        z.literal(GatewayLifecycleResponseType.VALOR_RECONCILE),
        z.literal(GatewayLifecycleResponseType.VALOR_REFUND),
        z.literal(GatewayLifecycleResponseType.VALOR_SALE_COPY),
        z.literal(GatewayLifecycleResponseType.VALOR_SALE_COPY_HTTP_ERROR),
        z.literal(GatewayLifecycleResponseType.VALOR_SALE_COPY_INVALID_JSON),
        z.literal(GatewayLifecycleResponseType.VALOR_TRANSACTION_LIST),
        z.literal(GatewayLifecycleResponseType.VALOR_TRANSACTION_STATUS),
        z.literal(GatewayLifecycleResponseType.VALOR_TXN_FETCH),
        z.literal(GatewayLifecycleResponseType.VALOR_VC_PUBLISH),
        z.literal(GatewayLifecycleResponseType.VALOR_VC_STATUS),
        z.literal(GatewayLifecycleResponseType.VALOR_VOID),
    ]);

export const ProviderGatewayResponseSnapshotSchema: z.ZodType<ProviderGatewayResponseSnapshot> =
    JsonObjectSchema;

export const CloverWebhookPaymentRetrievalResponseSnapshotSchema: z.ZodType<CloverWebhookPaymentRetrievalResponseSnapshot> =
    z.object({ id: z.string().trim().min(1) }).catchall(JsonValueSchema);

export const StoredCloverWebhookPaymentRetrievalResponseSnapshotSchema: z.ZodType<CloverWebhookPaymentRetrievalResponseSnapshot> =
    z.custom<CloverWebhookPaymentRetrievalResponseSnapshot>(
        (value: unknown): value is CloverWebhookPaymentRetrievalResponseSnapshot => {
            const result = StoredGatewayResponseJsonObjectRuntimeSchema.safeParse(value);
            return result.success && z.string().trim().min(1).safeParse(result.data.id).success;
        },
        { message: 'Invalid stored Clover webhook payment retrieval response' }
    );

export const ValorCardMetadataSnapshotSchema: z.ZodType<ValorCardMetadataSnapshot> = z
    .object({
        CARD_BIN: z.string().optional(),
        CARD_TYPE: z.string().optional(),
        ISSUING_BANK: z.string().optional(),
        COUNTRY: z.string().optional(),
        CARD_LEVEL: z.string().optional(),
        CARD_CATEGORY: z.string().optional(),
    })
    .catchall(JsonValueSchema);

export const ValorTaxComponentSnapshotSchema: z.ZodType<ValorTaxComponentSnapshot> = z
    .object({
        rate: z.number(),
        amount: z.union([z.number(), z.string()]),
        taxableSales: z.union([z.number(), z.string()]),
    })
    .catchall(JsonValueSchema);

export const ValorTaxDetailsSnapshotSchema: z.ZodType<ValorTaxDetailsSnapshot> = z
    .object({
        stateTax: ValorTaxComponentSnapshotSchema.optional(),
        cityTax: ValorTaxComponentSnapshotSchema.optional(),
        reducedTax: ValorTaxComponentSnapshotSchema.optional(),
    })
    .catchall(JsonValueSchema);

export const ValorTxnFetchRecordSnapshotSchema: z.ZodType<ValorTxnFetchRecordSnapshot> = z
    .object({
        REF_TXN_ID: z.string().optional(),
        TXN_ID: z.string().optional(),
        BASE_AMOUNT: z.string().optional(),
        NET_AMOUNT: z.string().optional(),
        CUSTOM_FEE_AMOUNT: z.string().optional(),
        TOTAL_TAX: z.union([z.string(), z.number()]).optional(),
        IS_VOID: z.string().optional(),
        IS_SETTLED: z.string().optional(),
        REFUND_AMOUNT: z.string().optional(),
        REFUND_COUNT: z.string().optional(),
        CARD_SCHEME: z.string().optional(),
        PAN: z.string().optional(),
        CARDHOLDER_NAME: z.string().optional(),
        AUTH_CODE: z.string().optional(),
        APPROVAL_CODE: z.string().optional(),
        RRN: z.string().optional(),
        STAN_NO: z.string().optional(),
        BATCH_NO: z.string().optional(),
        CARD_TYPE: z.string().optional(),
        CARD_METADATA: ValorCardMetadataSnapshotSchema.optional(),
        TAX_DETAILS: ValorTaxDetailsSnapshotSchema.optional(),
    })
    .catchall(JsonValueSchema);

export const ValorTxnFetchGatewayResponseSnapshotSchema: z.ZodType<ValorTxnFetchGatewayResponseSnapshot> =
    z
        .object({
            data: z.array(ValorTxnFetchRecordSnapshotSchema).min(1),
            error_no: z.string().optional(),
            status: z.string().optional(),
            number_of_records: z.number().optional(),
            meta: JsonObjectSchema.optional(),
        })
        .catchall(JsonValueSchema);

export const ValorVcStatusGatewayResponseSnapshotSchema: z.ZodType<ValorVcStatusGatewayResponseSnapshot> =
    z
        .object({
            response: z
                .object({
                    REQ_TXN_ID: z.string().optional(),
                    STATE: z.string().optional(),
                    EPI: z.string().optional(),
                    SERIAL_NO: z.string().optional(),
                    STAN_ID: z.string().optional(),
                    INVOICENUMBER: z.string().optional(),
                    INITIATOR_NAME: z.string().optional(),
                    AMOUNT: z.string().optional(),
                    PARTIAL: z.string().optional(),
                    TOTAL_AMOUNT: z.string().optional(),
                    ISSUER: z.string().optional(),
                    TRAN_TYPE: z.string().optional(),
                    MASKED_PAN: z.string().optional(),
                    RRN: z.string().optional(),
                    CODE: z.string().optional(),
                    BATCH_NO: z.string().optional(),
                    CARDHOLDER_NAME: z.string().optional(),
                    TXN_ID: z.string().optional(),
                    TRAN_METHOD: z.string().optional(),
                })
                .catchall(JsonValueSchema),
            error_no: z.string().optional(),
        })
        .catchall(JsonValueSchema);

export const GatewayLifecycleResponseJsonBodySchema: z.ZodType<GatewayLifecycleResponseJsonBody> =
    z.union([
        ValorTxnFetchGatewayResponseSnapshotSchema,
        ValorVcStatusGatewayResponseSnapshotSchema,
        ProviderGatewayResponseSnapshotSchema,
    ]);

export const ValorTxnFetchGatewayLifecycleResponseSchema: z.ZodType<
    Extract<
        GatewayLifecycleResponse,
        {
            readonly gateway_response_type: typeof GatewayLifecycleResponseType.VALOR_TXN_FETCH;
        }
    >
> = gatewayLifecycleResponseVariantSchema(
    GatewayLifecycleResponseType.VALOR_TXN_FETCH,
    ValorTxnFetchGatewayResponseSnapshotSchema
);

export const ValorVcStatusGatewayLifecycleResponseSchema: z.ZodType<
    Extract<
        GatewayLifecycleResponse,
        {
            readonly gateway_response_type: typeof GatewayLifecycleResponseType.VALOR_VC_STATUS;
        }
    >
> = gatewayLifecycleResponseVariantSchema(
    GatewayLifecycleResponseType.VALOR_VC_STATUS,
    ValorVcStatusGatewayResponseSnapshotSchema
);

export const ValorSaleCopyInvalidJsonGatewayLifecycleResponseSchema: z.ZodType<
    Extract<
        GatewayLifecycleResponse,
        {
            readonly gateway_response_type: typeof GatewayLifecycleResponseType.VALOR_SALE_COPY_INVALID_JSON;
        }
    >
> = gatewayLifecycleResponseVariantSchema(
    GatewayLifecycleResponseType.VALOR_SALE_COPY_INVALID_JSON,
    z.null()
);

export const CloverChargeInvalidJsonGatewayLifecycleResponseSchema: z.ZodType<
    Extract<
        GatewayLifecycleResponse,
        {
            readonly gateway_response_type: typeof GatewayLifecycleResponseType.CLOVER_CHARGE_INVALID_JSON;
        }
    >
> = gatewayLifecycleResponseVariantSchema(
    GatewayLifecycleResponseType.CLOVER_CHARGE_INVALID_JSON,
    z.null()
);

export const CloverChargeGatewayLifecycleResponseSchema: z.ZodType<
    Extract<
        GatewayLifecycleResponse,
        {
            readonly gateway_response_type: typeof GatewayLifecycleResponseType.CLOVER_CHARGE;
        }
    >
> = gatewayLifecycleResponseVariantSchema(
    GatewayLifecycleResponseType.CLOVER_CHARGE,
    CloverCnpTokenSaleChargeBodySchema
);

export const CloverPreflightGatewayLifecycleResponseSchema: z.ZodType<
    Extract<
        GatewayLifecycleResponse,
        {
            readonly gateway_response_type: typeof GatewayLifecycleResponseType.CLOVER_LIFECYCLE_PREFLIGHT;
        }
    >
> = gatewayLifecycleResponseVariantSchema(
    GatewayLifecycleResponseType.CLOVER_LIFECYCLE_PREFLIGHT,
    CloverLifecyclePreflightResponsePayloadSchema
);

export const CloverFinalizationGatewayLifecycleResponseSchema: z.ZodType<
    Extract<
        GatewayLifecycleResponse,
        {
            readonly gateway_response_type: typeof GatewayLifecycleResponseType.CLOVER_LIFECYCLE_FINALIZATION;
        }
    >
> = gatewayLifecycleResponseVariantSchema(
    GatewayLifecycleResponseType.CLOVER_LIFECYCLE_FINALIZATION,
    CloverLifecycleFinalizationResponsePayloadSchema
);

export const CloverPaymentGatewayLifecycleResponseSchema: z.ZodType<
    Extract<
        GatewayLifecycleResponse,
        {
            readonly gateway_response_type: typeof GatewayLifecycleResponseType.CLOVER_PAYMENT;
        }
    >
> = gatewayLifecycleResponseVariantSchema(
    GatewayLifecycleResponseType.CLOVER_PAYMENT,
    CloverTransactionDetailsPaymentBodySchema
);

export const CloverExternalPaymentGatewayLifecycleResponseSchema: z.ZodType<
    Extract<
        GatewayLifecycleResponse,
        {
            readonly gateway_response_type: typeof GatewayLifecycleResponseType.CLOVER_PAYMENT_EXTERNAL;
        }
    >
> = gatewayLifecycleResponseVariantSchema(
    GatewayLifecycleResponseType.CLOVER_PAYMENT_EXTERNAL,
    CloverTransactionDetailsPaymentBodySchema
);

export const CloverRefundGatewayLifecycleResponseSchema: z.ZodType<
    Extract<
        GatewayLifecycleResponse,
        {
            readonly gateway_response_type: typeof GatewayLifecycleResponseType.CLOVER_REFUND;
        }
    >
> = gatewayLifecycleResponseVariantSchema(
    GatewayLifecycleResponseType.CLOVER_REFUND,
    CloverRefundResponseDetailsSchema
);

export const CloverTerminalSaleGatewayLifecycleResponseSchema: z.ZodType<
    Extract<
        GatewayLifecycleResponse,
        {
            readonly gateway_response_type: typeof GatewayLifecycleResponseType.CLOVER_TERMINAL_SALE;
        }
    >
> = gatewayLifecycleResponseVariantSchema(
    GatewayLifecycleResponseType.CLOVER_TERMINAL_SALE,
    CloverTerminalSaleResponseLifecyclePayloadSchema
);

export const CloverVoidGatewayLifecycleResponseSchema: z.ZodType<
    Extract<
        GatewayLifecycleResponse,
        {
            readonly gateway_response_type: typeof GatewayLifecycleResponseType.CLOVER_VOID;
        }
    >
> = gatewayLifecycleResponseVariantSchema(
    GatewayLifecycleResponseType.CLOVER_VOID,
    CloverVoidResponseDetailsSchema
);

export const CloverWebhookPaymentRetrievalGatewayLifecycleResponseSchema: z.ZodType<
    Extract<
        GatewayLifecycleResponse,
        {
            readonly gateway_response_type: typeof GatewayLifecycleResponseType.CLOVER_WEBHOOK_PAYMENT_RETRIEVAL;
        }
    >
> = gatewayLifecycleResponseVariantSchema(
    GatewayLifecycleResponseType.CLOVER_WEBHOOK_PAYMENT_RETRIEVAL,
    CloverWebhookPaymentRetrievalResponseSnapshotSchema
);

export const GatewayLifecycleDiagnosticResponseSchema: z.ZodType<
    Extract<GatewayLifecycleResponse, { readonly gateway_response_json: JsonObject }>
> = z.object({
    gateway_response_type: GatewayLifecycleResponseTypeSchema.refine(
        (value) =>
            value !== GatewayLifecycleResponseType.VALOR_TXN_FETCH &&
            value !== GatewayLifecycleResponseType.VALOR_VC_STATUS &&
            value !== GatewayLifecycleResponseType.VALOR_SALE_COPY_INVALID_JSON &&
            value !== GatewayLifecycleResponseType.CLOVER_CHARGE_INVALID_JSON &&
            value !== GatewayLifecycleResponseType.CLOVER_CHARGE &&
            value !== GatewayLifecycleResponseType.CLOVER_LIFECYCLE_PREFLIGHT &&
            value !== GatewayLifecycleResponseType.CLOVER_LIFECYCLE_FINALIZATION &&
            value !== GatewayLifecycleResponseType.CLOVER_PAYMENT &&
            value !== GatewayLifecycleResponseType.CLOVER_PAYMENT_EXTERNAL &&
            value !== GatewayLifecycleResponseType.CLOVER_REFUND &&
            value !== GatewayLifecycleResponseType.CLOVER_TERMINAL_SALE &&
            value !== GatewayLifecycleResponseType.CLOVER_VOID &&
            value !== GatewayLifecycleResponseType.CLOVER_WEBHOOK_PAYMENT_RETRIEVAL,
        'Diagnostic gateway response type cannot use a semantic response type'
    ),
    gateway_response_json: ProviderGatewayResponseSnapshotSchema,
});

export const GatewayLifecycleResponseSchema: z.ZodType<GatewayLifecycleResponse> = z.union([
    ValorTxnFetchGatewayLifecycleResponseSchema,
    ValorVcStatusGatewayLifecycleResponseSchema,
    ValorSaleCopyInvalidJsonGatewayLifecycleResponseSchema,
    CloverChargeInvalidJsonGatewayLifecycleResponseSchema,
    CloverChargeGatewayLifecycleResponseSchema,
    CloverPreflightGatewayLifecycleResponseSchema,
    CloverFinalizationGatewayLifecycleResponseSchema,
    CloverPaymentGatewayLifecycleResponseSchema,
    CloverExternalPaymentGatewayLifecycleResponseSchema,
    CloverRefundGatewayLifecycleResponseSchema,
    CloverTerminalSaleGatewayLifecycleResponseSchema,
    CloverVoidGatewayLifecycleResponseSchema,
    CloverWebhookPaymentRetrievalGatewayLifecycleResponseSchema,
    GatewayLifecycleDiagnosticResponseSchema,
]);

export const StoredCloverChargeGatewayLifecycleResponseSchema: z.ZodType<GatewayLifecycleResponse> =
    gatewayLifecycleResponseVariantSchema(
        GatewayLifecycleResponseType.CLOVER_CHARGE,
        StoredCloverChargeResponseSchema
    );

export const StoredCloverChargeHttpErrorGatewayLifecycleResponseSchema: z.ZodType<GatewayLifecycleResponse> =
    gatewayLifecycleResponseVariantSchema(
        GatewayLifecycleResponseType.CLOVER_CHARGE_HTTP_ERROR,
        StoredProviderGatewayResponseSnapshotSchema
    );

export const StoredCloverPreflightGatewayLifecycleResponseSchema: z.ZodType<GatewayLifecycleResponse> =
    gatewayLifecycleResponseVariantSchema(
        GatewayLifecycleResponseType.CLOVER_LIFECYCLE_PREFLIGHT,
        StoredCloverLifecyclePreflightResponseSchema
    );

export const StoredCloverFinalizationGatewayLifecycleResponseSchema: z.ZodType<GatewayLifecycleResponse> =
    gatewayLifecycleResponseVariantSchema(
        GatewayLifecycleResponseType.CLOVER_LIFECYCLE_FINALIZATION,
        StoredCloverLifecycleFinalizationResponseSchema
    );

export const StoredCloverPaymentGatewayLifecycleResponseSchema: z.ZodType<GatewayLifecycleResponse> =
    gatewayLifecycleResponseVariantSchema(
        GatewayLifecycleResponseType.CLOVER_PAYMENT,
        StoredCloverTransactionDetailsPaymentBodySchema
    );

export const StoredCloverExternalPaymentGatewayLifecycleResponseSchema: z.ZodType<GatewayLifecycleResponse> =
    gatewayLifecycleResponseVariantSchema(
        GatewayLifecycleResponseType.CLOVER_PAYMENT_EXTERNAL,
        StoredCloverTransactionDetailsPaymentBodySchema
    );

export const StoredCloverExternalPaymentHttpErrorGatewayLifecycleResponseSchema: z.ZodType<GatewayLifecycleResponse> =
    gatewayLifecycleResponseVariantSchema(
        GatewayLifecycleResponseType.CLOVER_PAYMENT_EXTERNAL_HTTP_ERROR,
        StoredProviderGatewayResponseSnapshotSchema
    );

export const StoredCloverRefundGatewayLifecycleResponseSchema: z.ZodType<GatewayLifecycleResponse> =
    gatewayLifecycleResponseVariantSchema(
        GatewayLifecycleResponseType.CLOVER_REFUND,
        StoredCloverRefundResponseSchema
    );

export const StoredCloverTerminalSaleGatewayLifecycleResponseSchema: z.ZodType<GatewayLifecycleResponse> =
    gatewayLifecycleResponseVariantSchema(
        GatewayLifecycleResponseType.CLOVER_TERMINAL_SALE,
        StoredCloverTerminalSaleResponseSchema
    );

export const StoredCloverVoidGatewayLifecycleResponseSchema: z.ZodType<GatewayLifecycleResponse> =
    gatewayLifecycleResponseVariantSchema(
        GatewayLifecycleResponseType.CLOVER_VOID,
        StoredCloverVoidResponseSchema
    );

export const StoredCloverWebhookPaymentRetrievalGatewayLifecycleResponseSchema: z.ZodType<GatewayLifecycleResponse> =
    gatewayLifecycleResponseVariantSchema(
        GatewayLifecycleResponseType.CLOVER_WEBHOOK_PAYMENT_RETRIEVAL,
        StoredCloverWebhookPaymentRetrievalResponseSnapshotSchema
    );

export const StoredValorTxnFetchGatewayLifecycleResponseSchema: z.ZodType<GatewayLifecycleResponse> =
    gatewayLifecycleResponseVariantSchema(
        GatewayLifecycleResponseType.VALOR_TXN_FETCH,
        z.preprocess(
            normalizeStoredValorTxnFetchResponse,
            ValorTxnFetchGatewayResponseSnapshotSchema
        )
    );

export const StoredGatewayLifecycleResponseSchema: z.ZodType<GatewayLifecycleResponse> = z.union([
    StoredValorTxnFetchGatewayLifecycleResponseSchema,
    StoredCloverChargeGatewayLifecycleResponseSchema,
    StoredCloverChargeHttpErrorGatewayLifecycleResponseSchema,
    CloverChargeInvalidJsonGatewayLifecycleResponseSchema,
    StoredCloverPreflightGatewayLifecycleResponseSchema,
    StoredCloverFinalizationGatewayLifecycleResponseSchema,
    StoredCloverPaymentGatewayLifecycleResponseSchema,
    StoredCloverExternalPaymentGatewayLifecycleResponseSchema,
    StoredCloverExternalPaymentHttpErrorGatewayLifecycleResponseSchema,
    StoredCloverRefundGatewayLifecycleResponseSchema,
    StoredCloverTerminalSaleGatewayLifecycleResponseSchema,
    StoredCloverVoidGatewayLifecycleResponseSchema,
    StoredCloverWebhookPaymentRetrievalGatewayLifecycleResponseSchema,
    GatewayLifecycleResponseSchema,
]);

export function normalizeGatewayLifecycleResponseStorageV1(
    response: GatewayLifecycleResponse
): GatewayLifecycleResponse {
    return response;
}

export const GatewayResponseJsonStorageContract = defineStoredJsonContract({
    contractName: 'GatewayResponseJson',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'StoredGatewayLifecycleResponseSchema',
            schema: StoredGatewayLifecycleResponseSchema,
            schemaInput: (boundary: StoredJsonRowBoundary) => ({
                gateway_response_type: boundary.siblingValues.gateway_response_type,
                gateway_response_json: boundary.value,
            }),
            guards: [
                {
                    kind: 'sibling_field_present',
                    siblingField: 'gateway_response_type',
                },
            ],
            normalize: normalizeGatewayLifecycleResponseStorageV1,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v1',
});

export const StoredGatewayLifecycleResponseRowParser: z.ZodType<GatewayLifecycleResponse> =
    buildStoredJsonParser(GatewayResponseJsonStorageContract);

export function parseGatewayLifecycleResponseJsonParts(params: {
    responseType: string;
    payload: unknown;
    context: string;
}): GatewayLifecycleResponse {
    const parseResult = GatewayLifecycleResponseSchema.safeParse({
        gateway_response_type: params.responseType,
        gateway_response_json: params.payload,
    });
    if (!parseResult.success) {
        throw new Error(
            `Invalid gateway_response_json for ${params.context} type ${params.responseType}: ${parseResult.error.message}`
        );
    }
    return parseResult.data;
}

export function parseStoredGatewayLifecycleResponseJsonParts(params: {
    responseType: string;
    payload: unknown;
    context: string;
}): GatewayLifecycleResponse {
    const parseResult = StoredGatewayLifecycleResponseSchema.safeParse({
        gateway_response_type: params.responseType,
        gateway_response_json: params.payload,
    });
    if (!parseResult.success) {
        throw new Error(
            `Invalid stored gateway_response_json for ${params.context} type ${params.responseType}: ${parseResult.error.message}`
        );
    }
    return parseResult.data;
}
